import React, { useState } from 'react'
import classNames from 'classnames'
import { Bomb, Clock, Flame, Gem, Ghost, Package, Skull, TrendingUp, Wallet } from 'lucide-react'
import { LOGO_SRC } from '../config/brand'
import { FOGS } from './playground/halloween/HalloweenFogs'
import { DECOR, HalloweenDecor, Pumpkin, SlimeEdge } from './playground/halloween/HalloweenDecor'
import './playground/halloween/halloween.css'

// Прототип хэллоуинского оформления: 5 вариантов тумана на фоне страницы,
// включаемый декор и примеры элементов интерфейса. Всё без файлов картинок.

const TILES = [
  { name: 'Кейсы', icon: Package, tone: 'rgba(255, 122, 26, 0.55)', border: 'border-orange-500/60' },
  { name: 'Апгрейд', icon: TrendingUp, tone: 'rgba(124, 255, 79, 0.4)', border: 'border-lime-400/50' },
  { name: 'Краш', icon: Flame, tone: 'rgba(255, 60, 60, 0.45)', border: 'border-red-500/50' },
  { name: 'Мины', icon: Bomb, tone: 'rgba(176, 38, 255, 0.5)', border: 'border-purple-500/60' }
]

const COST_STYLES = {
  'дёшево': 'text-lime-300 border-lime-400/30 bg-lime-400/10',
  'средне': 'text-amber-300 border-amber-400/30 bg-amber-400/10',
  'дорого': 'text-red-300 border-red-400/30 bg-red-400/10'
}

const Playground = () => {
  const [fogId, setFogId] = useState(FOGS[0].id)
  const [decor, setDecor] = useState({ webs: true, spider: true, bats: true, embers: true, lightning: false })
  const ActiveFog = FOGS.find(({ id }) => id === fogId).Component

  return (
    <div className="relative px-4 pb-10 pt-6 text-white lg:px-0">
      <ActiveFog key={fogId} />
      <HalloweenDecor enabled={decor} />

      <div className="mx-auto max-w-3xl space-y-6">
        <header className="pt-4 text-center">
          <h1 className="text-3xl text-orange-400 [text-shadow:0_0_24px_rgba(255,120,20,0.45)] lg:text-4xl">Хэллоуин</h1>
          <p className="mt-1 text-sm text-text-secondary">Туман и декор только кодом, без файлов картинок</p>
        </header>

        <section className="space-y-2">
          <h2 className="text-xs font-bold uppercase not-italic tracking-wide text-text-secondary">Туман</h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {FOGS.map(({ id, name, tech, cost }) => (
              <button
                key={id}
                type="button"
                onClick={() => setFogId(id)}
                className={classNames(
                  'block rounded-xl border p-3 text-left transition-all active:scale-[0.98]',
                  fogId === id ? 'border-orange-400/70 bg-orange-500/15 shadow-[0_0_20px_rgba(255,120,20,0.2)]' : 'border-white/10 bg-black/40 hover:border-white/25'
                )}
              >
                <span className="block text-sm font-bold">{name}</span>
                <span className="mt-0.5 block text-[11px] leading-tight text-text-secondary">{tech}</span>
                <span className={classNames('mt-2 inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', COST_STYLES[cost])}>{cost}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="space-y-2">
          <h2 className="text-xs font-bold uppercase not-italic tracking-wide text-text-secondary">Декор</h2>
          <div className="flex flex-wrap gap-2">
            {DECOR.map(({ id, name }) => (
              <button
                key={id}
                type="button"
                aria-pressed={decor[id]}
                onClick={() => setDecor((state) => ({ ...state, [id]: !state[id] }))}
                className={classNames(
                  'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all active:scale-[0.98]',
                  decor[id] ? 'border-purple-400/60 bg-purple-500/20 text-purple-100' : 'border-white/10 bg-black/40 text-text-secondary'
                )}
              >
                {name}
              </button>
            ))}
          </div>
        </section>

        <section className="py-4 text-center">
          <div className="relative inline-block">
            <img src={LOGO_SRC} alt="SKINOZ" className="relative z-10 mx-auto w-full max-w-[300px]" />
            <div className="pointer-events-none absolute left-1/2 top-[85%] h-8 w-3/4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-500/35 blur-2xl" />
          </div>
        </section>

        <section className="hw-card rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">Баланс</span>
            <Ghost size={18} className="text-purple-300/70" />
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Gem size={22} className="text-orange-400" />
            <span className="hw-flame-text text-3xl font-bold tabular-nums">12 450</span>
          </div>
          <div className="mt-4 flex gap-2">
            <button type="button" className="hw-cta flex-1 rounded-xl px-4 py-2.5 text-sm font-bold transition-all active:scale-[0.98]">Пополнить</button>
            <button type="button" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold transition-all hover:bg-white/10 active:scale-[0.98]">
              <Wallet size={16} />
              Вывести
            </button>
          </div>
        </section>

        <section className="relative overflow-hidden rounded-2xl border border-lime-400/30 bg-[linear-gradient(135deg,#1a0f24,#0d0a12_60%,#14200f)] p-4 pt-8">
          <SlimeEdge />
          <div className="flex items-center gap-4">
            <Pumpkin className="h-24 w-24 shrink-0 sm:h-28 sm:w-28" />
            <div className="min-w-0">
              <span className="text-[11px] font-bold uppercase tracking-wide text-lime-300">Ивент</span>
              <p className="mt-1 text-lg font-black uppercase leading-tight">Ночь тыкв</p>
              <p className="mt-1 text-sm text-text-secondary">Открывай кейсы — собирай тыквы и меняй их на призы</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="flex items-center gap-1 rounded-lg border border-red-500/40 bg-dark-primary/70 px-2 py-1 text-[11px] font-bold tabular-nums text-red-300">
              <Clock size={12} />
              02д 14:36:09
            </span>
            <span className="flex items-center gap-1 rounded-lg border border-orange-500/40 bg-dark-primary/70 px-2 py-1 text-[11px] font-bold tabular-nums text-orange-300">
              <Gem size={12} />
              Фонд 500 000
            </span>
            <span className="flex items-center gap-1 rounded-lg border border-purple-500/40 bg-dark-primary/70 px-2 py-1 text-[11px] font-bold text-purple-200">
              <Skull size={12} />
              Босс-кейс
            </span>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {TILES.map(({ name, icon: Icon, tone, border }) => (
            <button
              key={name}
              type="button"
              style={{ '--hw-tone': tone }}
              className={classNames('hw-tile block aspect-[3/4] rounded-2xl border-2 p-3 transition-all hover:scale-[1.02] active:scale-[0.98]', border)}
            >
              <span className="flex h-full flex-col items-center justify-end gap-3">
                <Icon size={40} className="text-white/90 drop-shadow-[0_0_12px_rgba(255,255,255,0.35)]" />
                <span className="text-base font-black uppercase">{name}</span>
              </span>
            </button>
          ))}
        </section>

        <button type="button" className="hw-cta block w-full rounded-xl p-5 text-base font-black uppercase transition-all active:scale-[0.98]">
          Открыть тыквенный кейс
        </button>
      </div>
    </div>
  )
}

export default Playground
